export type WikiImage = {
	/** путь в dnd2-images: `wiki/scag/azuth-symbol.webp` */
	path: string;
	width?: number;
	height?: number;
	author?: string;
	caption?: string;
};

/** Поля карточки, общие для всех статей */
export type WikiInfoboxCommon = {
	/** картинка в шапке карточки: символ бога, герб, вид города */
	image?: WikiImage;
	/** герб города или государства: в карточке под основной картинкой */
	emblem?: WikiImage;
	/** вид из раздела «Виды», которому соответствует расовая статья: Люди → Человек */
	specie?: {id: string; title: string};
	/** id статей союзов и организаций, в которые входит статья: Амфел → the-lords-alliance */
	memberOf?: string[];
	/** кусок карты Фейруна с отметкой места: его снимает скилл wiki-city-map */
	locator?: WikiImage;
};

export type WikiGodInfo = {
	epithets?: string;
	sphere?: string;
	alignment?: string;
	domains?: string;
	symbol?: string;
	pantheon?: string;
};

export type WikiSettlementInfo = {
	population?: string;
	ruler?: string;
	government?: string;
};

export type WikiRealmInfo = WikiSettlementInfo & {
	capital?: string;
	languages?: string;
};

export type WikiRegionInfo = {
	glance?: string;
	realms?: string;
	landmarks?: string;
	threats?: string;
};

export type WikiRaceInfo = {
	subraces?: string;
};

export type WikiHolidayInfo = {
	date?: string;
};

export type WikiPersonInfo = {
	patron?: string;
};

export type WikiOrganizationInfo = {
	leader?: string;
	headquarters?: string;
};

/**
 * Карточка статьи (поле infobox): общие поля и поля всех типов вперемешку.
 * Какие поля показывать, решает тип статьи — WIKI_TYPES; значения — строки с разметкой сайта.
 */
export type WikiInfobox = WikiInfoboxCommon &
	WikiGodInfo &
	WikiRealmInfo &
	WikiOrganizationInfo &
	WikiRaceInfo &
	WikiPersonInfo &
	WikiRegionInfo &
	WikiHolidayInfo;

export type WikiFieldKey = Exclude<keyof WikiInfobox, keyof WikiInfoboxCommon>;

export const WIKI_FIELDS: Record<WikiFieldKey, string> = {
	epithets: 'Прозвища',
	sphere: 'Сфера',
	alignment: 'Мировоззрение',
	domains: 'Домены',
	symbol: 'Символ',
	pantheon: 'Пантеон',
	capital: 'Столица',
	ruler: 'Правитель',
	leader: 'Лидер',
	government: 'Форма правления',
	population: 'Население',
	languages: 'Языки',
	headquarters: 'Штаб-квартира',
	subraces: 'Подрасы',
	patron: 'Покровитель колдунов',
	date: 'Дата',
	glance: 'Общая картина',
	realms: 'Сферы влияния',
	landmarks: 'Ориентиры',
	threats: 'Угрозы',
};

type WikiTypeInfo = {
	title: string;
	/** во множественном числе: заголовок списка статей этого типа в конце статьи */
	plural: string;
	fields: WikiFieldKey[];
	/** подпись родителя в карточке, если это не место: у события родитель — эпоха */
	parentLabel?: string;
	/** тип служебный: в карточке, списках и заголовке страницы не показывается */
	hidden?: boolean;
};

const SETTLEMENT: WikiFieldKey[] = ['ruler', 'government', 'population'];

export const WIKI_TYPES = {
	god: {title: 'Бог', plural: 'Боги', fields: ['epithets', 'sphere', 'alignment', 'domains', 'symbol', 'pantheon']},
	pantheon: {title: 'Пантеон', plural: 'Пантеоны', fields: []},
	world: {title: 'Мир', plural: 'Миры', fields: []},
	continent: {title: 'Континент', plural: 'Континенты', fields: []},
	region: {title: 'Регион', plural: 'Регионы', fields: ['glance', 'realms', 'languages', 'landmarks', 'threats']},
	realm: {
		title: 'Государство',
		plural: 'Государства',
		fields: ['capital', 'ruler', 'government', 'population', 'languages'],
	},
	island: {title: 'Остров', plural: 'Острова', fields: []},
	city: {title: 'Город', plural: 'Города', fields: SETTLEMENT},
	fortress: {title: 'Крепость', plural: 'Крепости', fields: SETTLEMENT},
	district: {title: 'Район', plural: 'Районы', fields: []},
	street: {title: 'Улица', plural: 'Улицы', fields: []},
	building: {title: 'Здание', plural: 'Здания', fields: []},
	place: {title: 'Место', plural: 'Места', fields: []},
	forest: {title: 'Лес', plural: 'Леса', fields: []},
	hills: {title: 'Холмы', plural: 'Холмы', fields: []},
	mountains: {title: 'Горы', plural: 'Горы', fields: []},
	swamp: {title: 'Болото', plural: 'Болота', fields: []},
	water: {title: 'Водоём', plural: 'Водоёмы', fields: []},
	organization: {title: 'Организация', plural: 'Организации', fields: ['leader', 'headquarters', 'alignment']},
	race: {title: 'Раса', plural: 'Расы', fields: ['subraces']},
	person: {title: 'Персонаж', plural: 'Персонажи', fields: ['patron', 'alignment']},
	concept: {title: 'Понятие', plural: 'Статьи', fields: [], hidden: true},
	era: {title: 'Эпоха', plural: 'Эпохи', fields: [], parentLabel: 'Раздел'},
	event: {title: 'Событие', plural: 'События', fields: [], parentLabel: 'Эпоха'},
	holiday: {title: 'Праздник', plural: 'Праздники', fields: ['date'], parentLabel: 'Где отмечают'},
} satisfies Record<string, WikiTypeInfo>;

export type WikiType = keyof typeof WIKI_TYPES;

export const WIKI_TYPE_OPTIONS = Object.entries(WIKI_TYPES).map(([value, info]) => ({value, label: info.title}));

export function isWikiType(type: string): type is WikiType {
	return type in WIKI_TYPES;
}

export function wikiTypeTitles(types?: string[] | null): string[] {
	return (types || [])
		.filter(isWikiType)
		.filter((type) => !(WIKI_TYPES[type] as WikiTypeInfo).hidden)
		.map((type) => WIKI_TYPES[type].title);
}

/** поля карточки для набора типов: без повторов, в порядке типов */
export function wikiParentLabel(types?: string[] | null): string {
	const own = (types || []).filter(isWikiType).map((type) => (WIKI_TYPES[type] as WikiTypeInfo).parentLabel);
	return own.find(Boolean) || 'Расположение';
}

export function wikiFields(types?: string[] | null): WikiFieldKey[] {
	const fields = (types || []).filter(isWikiType).flatMap((type) => WIKI_TYPES[type].fields as WikiFieldKey[]);
	return [...new Set(fields)];
}
